/**
 * TCPN PHASE S1-C1 — Clinic Admin: Logo upload + status surface.
 *
 * Single-purpose page. Lets a clinic admin upload a brand logo, see its
 * current moderation state (none / pending / approved / rejected /
 * superseded), replace it, and review history. NO public surface
 * rendering happens in this phase — the brief restricts logo display to
 * the owner-internal admin context only.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { formatDateTimeLocale } from '../../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { CheckCircle2, Clock, AlertTriangle, Upload, RefreshCw, ImageIcon } from 'lucide-react';
import AuthMediaThumb from '../../../components/AuthMediaThumb';

// Canonical moderation status → i18n key + visual config. Backend enum
// values (`none`/`pending`/`approved`/`rejected`/`superseded`) remain
// the SoT; only display chrome is localized.
const STATUS_BADGE_CFG = {
  none:       { key: 'none',       cls: 'bg-slate-100 text-slate-700',      icon: ImageIcon },
  pending:    { key: 'pending',    cls: 'bg-amber-100 text-amber-800',      icon: Clock },
  approved:   { key: 'approved',   cls: 'bg-emerald-100 text-emerald-800',  icon: CheckCircle2 },
  rejected:   { key: 'rejected',   cls: 'bg-rose-100 text-rose-800',        icon: AlertTriangle },
  superseded: { key: 'superseded', cls: 'bg-slate-200 text-slate-600',      icon: RefreshCw },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = STATUS_BADGE_CFG[status] || STATUS_BADGE_CFG.none;
  const Icon = cfg.icon;
  const label = t(`provider.media.logo.statuses.${cfg.key}`);
  return (
    <Badge className={`${cfg.cls} font-medium`} data-testid={`logo-status-badge-${status || 'none'}`}>
      <Icon className="h-3.5 w-3.5 mr-1" />
      {label}
    </Badge>
  );
}

/**
 * Authenticated <img> for a non-public preview. Delegates to the canonical
 * AuthMediaThumb (MEDIA-001 unification) so logo previews share the same
 * stability hardening (AbortController, fallback chain, deferred revoke).
 */
function AuthLogoImg({ mediaId, alt }) {
  return (
    <AuthMediaThumb
      mediaId={mediaId}
      alt={alt}
      variants={['preview', 'thumbnail', 'original']}
      className="w-full h-full object-contain"
      testIdPrefix="logo-preview"
    />
  );
}

export default function ClinicLogoPage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState('none');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, hist] = await Promise.all([
        api.get('/clinic/logo/current'),
        api.get('/clinic/logo/history'),
      ]);
      setCurrent(cur.data?.logo || null);
      setStatus(cur.data?.status || 'none');
      setHistory(hist.data?.items || []);
    } catch (e) {
      toast.error(t('provider.media.logo.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error(t('provider.media.logo.errors.tooLarge'));
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error(t('provider.media.logo.errors.unsupported'));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/clinic/logo/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        toast.success(t('provider.media.logo.toasts.submitted'));
        await refresh();
      } else {
        toast.error(res.data?.message || res.data?.error || t('provider.media.logo.errors.uploadFailed'));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('provider.media.logo.errors.uploadFailed');
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="clinic-logo-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t('provider.media.logo.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('provider.media.logo.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="logo-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> {t('provider.media.logo.refresh')}
        </Button>
      </div>

      <Card data-testid="logo-current-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            {t('provider.media.logo.current')}
            <StatusBadge status={status} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
            <div className="aspect-square bg-slate-50 border rounded-lg overflow-hidden" data-testid="logo-current-preview">
              {current?.media_id ? (
                <AuthLogoImg mediaId={current.media_id} alt={t('provider.media.logo.alt.logo')} />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                  <ImageIcon className="h-10 w-10 mb-2" />
                  <span className="text-xs">{t('provider.media.logo.noLogoYet')}</span>
                </div>
              )}
            </div>
            <div className="space-y-3 text-sm">
              {status === 'none' && (
                <p className="text-slate-600">
                  {t('provider.media.logo.helperNone')}
                </p>
              )}
              {status === 'pending' && (
                <p className="text-amber-700">
                  {t('provider.media.logo.helperPending')}
                </p>
              )}
              {status === 'approved' && (
                <p className="text-emerald-700">
                  {t('provider.media.logo.helperApproved')}
                </p>
              )}
              {status === 'rejected' && (
                <>
                  <p className="text-rose-700">{t('provider.media.logo.helperRejected')}</p>
                  {current?.moderation_notes && (
                    <p className="text-xs text-slate-600 italic" data-testid="logo-rejection-note">
                      {t('provider.media.logo.reviewerNote', { note: current.moderation_notes })}
                    </p>
                  )}
                  <p className="text-slate-600">{t('provider.media.logo.correctPrompt')}</p>
                </>
              )}
              {current?.uploaded_at && (
                <div className="text-xs text-slate-500">
                  {t('provider.media.logo.uploadedAt', { date: formatDateTimeLocale(current.uploaded_at) })}
                  {current.width && current.height && ` · ${current.width}×${current.height}px`}
                  {current.size_bytes && ` · ${(current.size_bytes / 1024).toFixed(0)} KB`}
                </div>
              )}

              <div className="pt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="logo-upload-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="logo-upload-btn"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploading
                    ? t('provider.media.logo.uploading')
                    : current?.media_id
                      ? t('provider.media.logo.replace')
                      : t('provider.media.logo.upload')}
                </Button>
                <p className="text-[11px] text-slate-400 mt-2">
                  {t('provider.media.logo.constraints')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card data-testid="logo-history-card">
          <CardHeader>
            <CardTitle className="text-base">{t('provider.media.logo.historyTitle', { count: history.length })}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((h) => (
                <div
                  key={h.media_id}
                  className="flex items-center gap-3 p-2 border rounded text-xs"
                  data-testid={`logo-history-row-${h.media_id}`}
                >
                  <div className="w-12 h-12 bg-slate-50 border rounded overflow-hidden shrink-0">
                    <AuthLogoImg mediaId={h.media_id} alt={t('provider.media.logo.historyAlt')} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={h.moderation_status} />
                      <span className="text-slate-500">
                        {formatDateTimeLocale(h.uploaded_at)}
                      </span>
                    </div>
                    {h.moderation_notes && (
                      <div className="text-slate-500 italic mt-1">
                        {t('provider.media.logo.reviewerNote', { note: h.moderation_notes })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
